import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Header } from '../components/header';
import {
  Loader2,
  Route,
  Map,
  ArrowLeft,
  AlertTriangle,
  Navigation,
  MapPin,
} from 'lucide-react';
import { useTranslationWithLanguage } from '../context/language-context';
import { InspectionCard } from '../components/inspection-card';
import { useAuth } from '../context/auth-context';
import { Button } from '../components/ui/button';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';

// Define BASE_URL from environment variables
const BASE_URL = import.meta.env.VITE_BASE_URL;

// Define the structure for establishments with location data for route optimization
interface Establishment {
  id: string;
  establishment_id: string;
  establishment: {
    id: string;
    name_en: string;
    name_ar: string;
  };
  emirate: {
    id: number;
    name_en: string;
    name_ar: string;
  };
  inspection_type: {
    id: number;
    name_en: string;
    name_ar: string;
  };
  risk_grade: string;
  status: string;
  location: {
    lat: number;
    lng: number;
  };
  distance?: number; // Distance from previous point in km
}

export default function RouteOptimizationPage() {
  const navigate = useNavigate();
  const { t, language } = useTranslationWithLanguage();
  const { getAccessToken } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [assignedTasks, setAssignedTasks] = useState<Establishment[]>([]);
  const [userLocation, setUserLocation] = useState<{
    lat: number;
    lng: number;
  } | null>(null);
  const [isCalculatingRoute, setIsCalculatingRoute] = useState(false);
  const [optimizedRoute, setOptimizedRoute] = useState<Establishment[]>([]);
  const [routeInfo, setRouteInfo] = useState<{
    totalDistance: number;
    totalTime: number;
    optimizationMethod: string;
  } | null>(null);

  // Navigation mode states
  const [isNavigationMode, setIsNavigationMode] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [navigationDirections, setNavigationDirections] = useState<string[]>([]);
  const [currentSegment, setCurrentSegment] = useState(0);
  const userMarkerRef = useRef<mapboxgl.Marker | null>(null);

  // Map related states and refs
  const mapContainer = useRef<HTMLDivElement | null>(null);
  const map = useRef<mapboxgl.Map | null>(null);
  const [mapLoaded, setMapLoaded] = useState(false);

  // Fetch assigned tasks from API
  useEffect(() => {
    const fetchAssignedTasks = async () => {
      const currentToken = getAccessToken();
      if (!currentToken) {
        setError(t('errors.notAuthenticated'));
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        // Fetch assigned tasks (status 6)
        await fetchTasksByStatus('6', currentToken);
      } catch (err) {
        console.error(`Error fetching assigned tasks:`, err);
        setError(
          err instanceof Error
            ? err.message
            : t('errors.unknownError')
        );
      } finally {
        setLoading(false);
      }
    };

    // Helper function to fetch tasks by status
    const fetchTasksByStatus = async (
      taskStatusId: string,
      token: string
    ) => {
      const conditionParam = {
        rules: [
          {
            operator: 'in',
            field: 'task_status_id',
            value: [taskStatusId],
          },
        ],
      };

      const params = new URLSearchParams({
        condition: JSON.stringify(conditionParam),
      });

      const response = await fetch(
        `${BASE_URL}/jsonapi/transform/task_list?${params.toString()}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error(
          `${t('errors.failedToFetch')} (${response.status} ${
            response.statusText
          })`
        );
      }

      const data = await response.json();

      // Debug the API response
      console.log('API Response:', data);

      // Assuming data.data contains the array of inspections
      if (data && data.data && Array.isArray(data.data)) {
        // Log the first item to understand the structure
        if (data.data.length > 0) {
          console.log('First task item:', data.data[0]);
        }

        const mappedEstablishments = data.data.map((item: any) => {
          // Check if establishment data exists and has coordinates
          const hasValidCoordinates =
            item.establishment &&
            item.establishment.latitude &&
            item.establishment.longitude;

          // Log any items with missing coordinates
          if (!hasValidCoordinates) {
            console.warn(`Task ${item.id} has missing or invalid coordinates`);
          }

          return {
            id: item.task_id || item.id,
            establishment_id: item.establishment_id,
            establishment: {
              id: item.establishment_id,
              name_en: item.establishment?.name_en || item.establishment_name_en || t('common.unknown', 'Unknown'),
              name_ar: item.establishment?.name_ar || item.establishment_name_ar || t('common.unknownAr', 'غير معروف'),
            },
            emirate: {
              id: item.emirate_id || 0,
              name_en: item.emirate_display || item.emirate_name_en || t('common.dubai', 'Dubai'),
              name_ar: item.emirate_display || item.emirate_name_ar || t('common.dubaiAr', 'دبي'),
            },
            inspection_type: {
              id: item.params?.inspection_type_id || 0,
              name_en: item.params?.inspection_type_display || item.inspection_type_name_en || t('inspections.regularInspection', 'Regular Inspection'),
              name_ar: item.params?.inspection_type_display || item.inspection_type_name_ar || t('inspections.regularInspection', 'تفتيش منتظم'),
            },
            risk_grade: calculateRiskGrade(item),
            status: 'assigned',
            location: {
              lat: hasValidCoordinates ? parseFloat(item.establishment.latitude) : 25.197197,
              lng: hasValidCoordinates ? parseFloat(item.establishment.longitude) : 55.274376,
            },
          };
        });

        setAssignedTasks(mappedEstablishments);

        // Calculate route after assigned tasks are loaded
        if (mappedEstablishments.length > 0) {
          calculateOptimalRoute();
        } else {
          setMapLoaded(true);
        }
      } else {
        setMapLoaded(true);
        console.warn('Unexpected API response structure:', data);
        setAssignedTasks([]);
      }
    };

    fetchAssignedTasks();
  }, [getAccessToken, t]);

  // Function to get user's current location
  const getUserLocation = () => {
    return new Promise<{ lat: number; lng: number }>(
      (resolve, reject) => {
        if (navigator.geolocation) {
          navigator.geolocation.getCurrentPosition(
            (position) => {
              const userLoc = {
                lat: position.coords.latitude,
                lng: position.coords.longitude,
              };
              setUserLocation(userLoc);
              resolve(userLoc);
            },
            (error) => {
              console.error('Error getting location:', error);
              // Default to a location in Dubai if geolocation fails
              const defaultLocation = {
                lat: 25.197197,
                lng: 55.274376,
              };
              setUserLocation(defaultLocation);
              resolve(defaultLocation);
            },
            { enableHighAccuracy: true, timeout: 5000, maximumAge: 0 }
          );
        } else {
          console.error(
            t('map.geolocationNotSupported', 'Geolocation is not supported by this browser.')
          );
          // Default to a location in Dubai if geolocation is not supported
          const defaultLocation = { lat: 25.197197, lng: 55.274376 };
          setUserLocation(defaultLocation);
          resolve(defaultLocation);
        }
      }
    );
  };

  const calculateRiskGrade = (item: any): string => {
    if (item.params.risk_category) return item.params.risk_category;
    return 'HIGH';
  };

  // Function to calculate distance between two points using Haversine formula
  const calculateDistance = (
    lat1: number,
    lon1: number,
    lat2: number,
    lon2: number
  ) => {
    const R = 6371; // Radius of the earth in km
    const dLat = ((lat2 - lat1) * Math.PI) / 180;
    const dLon = ((lon2 - lon1) * Math.PI) / 180;
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos((lat1 * Math.PI) / 180) *
        Math.cos((lat2 * Math.PI) / 180) *
        Math.sin(dLon / 2) *
        Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    const distance = R * c; // Distance in km
    return distance;
  };

  // Function to fetch route from Graphhopper API
  const fetchRouteFromGraphhopper = async (
    points: { lat: number; lng: number }[]
  ) => {
    if (points.length < 2) {
      console.error(t('route.needMorePoints', 'Need at least 2 points to calculate a route'));
      return null;
    }

    try {
      // Construct the points parameter in the format required by Graphhopper
      const pointsParam = points
        .map((point) => `point=${point.lat},${point.lng}`)
        .join('&');

      // Graphhopper API endpoint with API key
      // Note: In a production app, this key should be stored in an environment variable
      const url = `https://graphhopper.com/api/1/route?${pointsParam}&vehicle=car&locale=en&calc_points=true&points_encoded=false&key=67d5dbb6-edc5-4553-a859-c76b6233c900`;

      console.log('Fetching route from Graphhopper API with points:', points);

      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(t('route.graphhopperError', 'Graphhopper API error: {{status}} {{statusText}}', { status: response.status, statusText: response.statusText }));
      }

      const data = await response.json();
      console.log('Graphhopper API response:', data);

      return data;
    } catch (error) {
      console.error(t('route.fetchError', 'Error fetching route from Graphhopper:'), error);
      // If Graphhopper API fails, fall back to calculating distances using Haversine formula
      console.log(t('route.fallingBackToDirectCalculation', 'Falling back to direct distance calculation'));

      // Calculate direct distances between points
      const fallbackData = {
        paths: [{
          distance: 0,
          time: 0,
          points: {
            coordinates: points.map(p => [p.lng, p.lat])
          },
          distances: [] as number[]
        }]
      };

      // Calculate distances between consecutive points
      let totalDistance = 0;
      for (let i = 1; i < points.length; i++) {
        const prevPoint = points[i-1];
        const currPoint = points[i];
        const distance = calculateDistance(
          prevPoint.lat, prevPoint.lng,
          currPoint.lat, currPoint.lng
        );
        fallbackData.paths[0].distances.push(distance * 1000); // Convert to meters
        totalDistance += distance;
      }

      // Set total distance and estimated time (assuming 50 km/h average speed)
      fallbackData.paths[0].distance = totalDistance * 1000; // Convert to meters
      fallbackData.paths[0].time = (totalDistance / 50) * 60 * 60 * 1000; // Convert to milliseconds

      console.log('Created fallback route data:', fallbackData);
      return fallbackData;
    }
  };

  // Function to optimize route using GraphHopper's Route Optimization API
  const optimizeRouteWithGraphhopper = async (
    startLocation: { lat: number; lng: number },
    locations: { id: string; lat: number; lng: number }[]
  ) => {
    if (locations.length === 0) {
      console.error(t('route.noLocationsToOptimize', 'No locations to optimize'));
      return null;
    }

    try {
      // GraphHopper Route Optimization API endpoint
      const url = 'https://graphhopper.com/api/1/vrp?key=67d5dbb6-edc5-4553-a859-c76b6233c900';

      // Prepare the request payload according to GraphHopper's VRP API
      const payload = {
        vehicles: [
          {
            vehicle_id: "inspector_vehicle",
            start_address: {
              location_id: "start",
              lon: startLocation.lng,
              lat: startLocation.lat
            },
            end_address: {
              location_id: "start", // Return to start location
              lon: startLocation.lng,
              lat: startLocation.lat
            },
            type_id: "car"
          }
        ],
        services: locations.map((loc, index) => ({
          id: loc.id,
          name: `inspection_${index + 1}`,
          address: {
            location_id: loc.id,
            lon: loc.lng,
            lat: loc.lat
          },
          duration: 0, // No service time at each location
          size: [0] // No capacity constraints
        })),
        configuration: {
          routing: {
            calc_points: true,
            consider_traffic: true,
            network_data_provider: "openstreetmap",
            return_loads: false,
            fail_fast: false
          },
          algorithm: {
            problem_type: "min-max"
          }
        }
      };

      console.log('Sending route optimization request to GraphHopper:', payload);

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('GraphHopper optimization error response:', errorText);
        throw new Error(t('route.graphhopperOptimizationError', 'GraphHopper optimization API error: {{status}} {{statusText}}', {
          status: response.status,
          statusText: response.statusText
        }));
      }

      const data = await response.json();
      console.log('GraphHopper optimization API response:', data);

      return data;
    } catch (error) {
      console.error(t('route.optimizationError', 'Error optimizing route with GraphHopper:'), error);
      return null;
    }
  };

  // Fallback function to calculate route using nearest-neighbor algorithm
  const calculateRouteWithNearestNeighbor = async (
    startLocation: { lat: number; lng: number },
    establishmentGroups: { [key: string]: Establishment[] }
  ) => {
    try {
      // Implement nearest-neighbor algorithm to find the optimal route
      // Start with the user's current location
      let currentLocation = startLocation;
      const visitedEstablishmentIds = new Set<string>();
      const optimizedEstablishments: Establishment[] = [];
      const optimizedPoints: { lat: number; lng: number }[] = [startLocation];

      // Get all establishment groups as an array
      const allEstablishmentGroups = Object.values(establishmentGroups);

      // Continue until all establishments are visited
      while (visitedEstablishmentIds.size < allEstablishmentGroups.length) {
        let nearestEstablishment: Establishment | null = null;
        let minDistance = Infinity;
        let nearestGroupIndex = -1;

        // Find the nearest unvisited establishment
        allEstablishmentGroups.forEach((group, index) => {
          const establishmentId = group[0].establishment_id;

          // Skip if already visited
          if (visitedEstablishmentIds.has(establishmentId)) {
            return;
          }

          // Calculate distance from current location to this establishment
          const establishment = group[0];
          const distance = calculateDistance(
            currentLocation.lat,
            currentLocation.lng,
            establishment.location.lat,
            establishment.location.lng
          );

          // Update nearest if this is closer
          if (distance < minDistance) {
            minDistance = distance;
            nearestEstablishment = establishment;
            nearestGroupIndex = index;
          }
        });

        // If we found a nearest establishment
        if (nearestEstablishment !== null && nearestGroupIndex !== -1) {
          // TypeScript safety check
          const establishment = nearestEstablishment as Establishment;
          const establishmentId = establishment.establishment_id;
          const group = allEstablishmentGroups[nearestGroupIndex];

          // Mark as visited
          visitedEstablishmentIds.add(establishmentId);

          // Add the first task with distance
          const firstTaskWithDistance = {
            ...group[0],
            distance: parseFloat(minDistance.toFixed(2)),
          };

          optimizedEstablishments.push(firstTaskWithDistance);
          optimizedPoints.push(firstTaskWithDistance.location);

          console.log(
            t('route.distanceToEstablishment', 'Distance to establishment {{name}}: {{distance}} km', {
              name: firstTaskWithDistance.establishment.name_en,
              distance: firstTaskWithDistance.distance
            })
          );

          // For remaining tasks in the same establishment, set distance to 0
          for (let i = 1; i < group.length; i++) {
            optimizedEstablishments.push({
              ...group[i],
              distance: 0, // Zero distance for tasks at the same establishment
            });
          }

          // Update current location for next iteration
          currentLocation = establishment.location;
        }
      }

      // Fetch the route from Graphhopper API using the optimized points
      const routeData = await fetchRouteFromGraphhopper(optimizedPoints);

      // Debug log to check the structure of the route data
      console.log('Route data structure:', JSON.stringify({
        hasPath: !!routeData?.paths,
        pathCount: routeData?.paths?.length,
        hasPoints: !!routeData?.paths?.[0]?.points,
        pointsFormat: typeof routeData?.paths?.[0]?.points,
        coordinatesCount: routeData?.paths?.[0]?.points?.coordinates?.length
      }));

      if (!routeData || !routeData.paths || routeData.paths.length === 0) {
        throw new Error(t('route.failedToGetRouteData', 'Failed to get route data from Graphhopper API'));
      }

      // Extract the path and points from the response
      const path = routeData.paths[0];
      const pathPoints = path.points.coordinates;

      // Log the first few coordinates to verify format
      console.log('First few coordinates:', pathPoints.slice(0, 3));

      // Extract total distance and time from the API response
      const routeTotalDistance = path.distance / 1000; // Convert to km
      const totalTime = path.time / (1000 * 60); // Convert to minutes

      // Set route info
      setRouteInfo({
        totalDistance: parseFloat(routeTotalDistance.toFixed(2)),
        totalTime: Math.round(totalTime),
        optimizationMethod: "Nearest Neighbor"
      });

      console.log(t('route.totalRouteInfo', 'Total route distance: {{distance}} km, time: {{time}} minutes', {
        distance: routeTotalDistance.toFixed(2),
        time: Math.round(totalTime)
      }));

      // Debug log to check distance values
      console.log(
        t('route.optimizedRouteWithDistances', 'Optimized route with distances:'),
        optimizedEstablishments.map((item) => ({
          id: item.id,
          establishment_id: item.establishment_id,
          establishment: item.establishment.name_en,
          distance: item.distance,
        }))
      );

      // Set the optimized route
      setOptimizedRoute(optimizedEstablishments);

      // Initialize or update map with the route data
      setTimeout(() => {
        initializeMap(optimizedEstablishments, startLocation, pathPoints);
      }, 500);

      return true;
    } catch (error) {
      console.error('Error calculating route with nearest-neighbor:', error);
      return false;
    }
  };

  // Function to calculate the optimal route using GraphHopper's route optimization
  const calculateOptimalRoute = async () => {
    setIsCalculatingRoute(true);

    try {
      // Get user's current location
      const startLocation = await getUserLocation();

      if (assignedTasks.length === 0) {
        setOptimizedRoute([]);
        setIsCalculatingRoute(false);
        setMapLoaded(true);
        return;
      }

      // Handle missing or invalid coordinates
      const tasksWithValidCoordinates = assignedTasks.map((task) => {
        if (
          !task.location ||
          !task.location.lat ||
          !task.location.lng
        ) {
          // Default to a location in Dubai if coordinates are missing
          return {
            ...task,
            location: { lat: 25.197197, lng: 55.274376 },
          };
        }
        return task;
      });

      // Group tasks by establishment_id to avoid duplicate locations
      const establishmentGroups: { [key: string]: Establishment[] } = {};

      tasksWithValidCoordinates.forEach((task) => {
        const establishmentId = task.establishment_id;
        if (!establishmentGroups[establishmentId]) {
          establishmentGroups[establishmentId] = [];
        }
        establishmentGroups[establishmentId].push(task);
      });

      console.log(
        `Grouped ${tasksWithValidCoordinates.length} tasks into ${
          Object.keys(establishmentGroups).length
        } establishments`
      );

      // Prepare locations for route optimization
      // We only need one location per establishment (first task in each group)
      const locationsForOptimization = Object.values(establishmentGroups).map(group => ({
        id: group[0].establishment_id,
        lat: group[0].location.lat,
        lng: group[0].location.lng
      }));

      console.log('Locations for optimization:', locationsForOptimization);

      // Use GraphHopper's route optimization API to find the optimal route
      const optimizationResult = await optimizeRouteWithGraphhopper(
        startLocation,
        locationsForOptimization
      );

      // If GraphHopper optimization fails, fall back to nearest-neighbor algorithm
      if (!optimizationResult || !optimizationResult.solution) {
        console.warn(t('route.optimizationFailed', 'GraphHopper route optimization failed, falling back to nearest-neighbor algorithm'));
        return calculateRouteWithNearestNeighbor(startLocation, establishmentGroups);
      }

      // Process the optimization result
      const solution = optimizationResult.solution;
      const route = solution.routes[0]; // We only have one vehicle

      // Extract the optimized order of visits from the solution
      const optimizedOrder: string[] = [];

      // Skip the first and last activities as they are the start/end location
      for (let i = 1; i < route.activities.length - 1; i++) {
        const activity = route.activities[i];
        optimizedOrder.push(activity.address.location_id);
      }

      console.log('Optimized order of establishments:', optimizedOrder);

      // Create the optimized establishments array based on the optimized order
      const optimizedEstablishments: Establishment[] = [];
      const optimizedPoints: { lat: number; lng: number }[] = [startLocation];

      // Add establishments in the optimized order
      optimizedOrder.forEach((establishmentId, index) => {
        const group = establishmentGroups[establishmentId];
        if (!group) return;

        // Get the distance from the previous point
        let distance = 0;
        if (index === 0) {
          // First establishment - distance from start location
          distance = calculateDistance(
            startLocation.lat,
            startLocation.lng,
            group[0].location.lat,
            group[0].location.lng
          );
        } else {
          // Distance from previous establishment
          const prevEstablishmentId = optimizedOrder[index - 1];
          const prevGroup = establishmentGroups[prevEstablishmentId];
          if (prevGroup) {
            distance = calculateDistance(
              prevGroup[0].location.lat,
              prevGroup[0].location.lng,
              group[0].location.lat,
              group[0].location.lng
            );
          }
        }

        // Add the first task with distance
        const firstTaskWithDistance = {
          ...group[0],
          distance: parseFloat(distance.toFixed(2)),
        };

        optimizedEstablishments.push(firstTaskWithDistance);
        optimizedPoints.push(firstTaskWithDistance.location);

        console.log(
          t('route.distanceToEstablishment', 'Distance to establishment {{name}}: {{distance}} km', {
            name: firstTaskWithDistance.establishment.name_en,
            distance: firstTaskWithDistance.distance
          })
        );

        // For remaining tasks in the same establishment, set distance to 0
        for (let i = 1; i < group.length; i++) {
          optimizedEstablishments.push({
            ...group[i],
            distance: 0, // Zero distance for tasks at the same establishment
          });
        }
      });

      // Get the detailed route path from GraphHopper
      const routeData = await fetchRouteFromGraphhopper(optimizedPoints);

      // Debug log to check the structure of the route data
      console.log('Route data structure:', JSON.stringify({
        hasPath: !!routeData?.paths,
        pathCount: routeData?.paths?.length,
        hasPoints: !!routeData?.paths?.[0]?.points,
        pointsFormat: typeof routeData?.paths?.[0]?.points,
        coordinatesCount: routeData?.paths?.[0]?.points?.coordinates?.length
      }));

      if (!routeData || !routeData.paths || routeData.paths.length === 0) {
        throw new Error(t('route.failedToGetRouteData', 'Failed to get route data from Graphhopper API'));
      }

      // Extract the path and points from the response
      const path = routeData.paths[0];
      const pathPoints = path.points.coordinates;

      // Log the first few coordinates to verify format
      console.log('First few coordinates:', pathPoints.slice(0, 3));

      // Extract total distance and time from the API response
      const routeTotalDistance = path.distance / 1000; // Convert to km
      const totalTime = path.time / (1000 * 60); // Convert to minutes

      // Set route info
      setRouteInfo({
        totalDistance: parseFloat(routeTotalDistance.toFixed(2)),
        totalTime: Math.round(totalTime),
        optimizationMethod: "GraphHopper VRP"
      });

      console.log(t('route.totalRouteInfo', 'Total route distance: {{distance}} km, time: {{time}} minutes', {
        distance: routeTotalDistance.toFixed(2),
        time: Math.round(totalTime)
      }));

      // Debug log to check distance values
      console.log(
        t('route.optimizedRouteWithDistances', 'Optimized route with distances:'),
        optimizedEstablishments.map((item) => ({
          id: item.id,
          establishment_id: item.establishment_id,
          establishment: item.establishment.name_en,
          distance: item.distance,
        }))
      );

      // Set the optimized route
      setOptimizedRoute(optimizedEstablishments);

      // Initialize or update map with the route data
      setTimeout(() => {
        initializeMap(optimizedEstablishments, startLocation, pathPoints);
      }, 500);
    } catch (error) {
      console.error('Error calculating route:', error);
    } finally {
      setIsCalculatingRoute(false);
    }
  };

  // Function to initialize the map with the optimized route
  const initializeMap = (
    route: Establishment[],
    startLocation: { lat: number; lng: number },
    pathPoints?: [number, number][]
  ) => {
    // Set Mapbox token
    mapboxgl.accessToken = 'pk.eyJ1IjoiaGlqaWFuZ3RhbyIsImEiOiJjampxcjFnb3E2NTB5M3BvM253ZHV5YjhjIn0.WneUon5qFigfJRJ3oaZ3Ow';

    // If map already exists, remove it
    if (map.current) {
      map.current.remove();
      map.current = null;
    }

    // Check if container exists
    if (!mapContainer.current) return;

    try {
      // Create bounds to fit all points
      const bounds = new mapboxgl.LngLatBounds();

      // Add user location to bounds
      bounds.extend([startLocation.lng, startLocation.lat]);

      // Add all establishment locations to bounds
      route.forEach((item) => {
        bounds.extend([item.location.lng, item.location.lat]);
      });

      // Create new map
      const newMap = new mapboxgl.Map({
        container: mapContainer.current,
        style: 'mapbox://styles/mapbox/streets-v12',
        center: [startLocation.lng, startLocation.lat],
        zoom: 10,
      });

      // Store map reference
      map.current = newMap;

      // Add load handler
      newMap.on('load', () => {
        setMapLoaded(true);

        // Add user location marker
        const userEl = document.createElement('div');
        userEl.className = 'user-location-marker';
        userEl.style.width = '20px';
        userEl.style.height = '20px';
        userEl.style.borderRadius = '50%';
        userEl.style.backgroundColor = '#4285F4';
        userEl.style.border = '3px solid white';
        userEl.style.boxShadow = '0 0 10px rgba(0,0,0,0.3)';

        new mapboxgl.Marker(userEl)
          .setLngLat([startLocation.lng, startLocation.lat])
          .setPopup(
            new mapboxgl.Popup({ offset: 25 }).setHTML(
              `<h3>${t('map.yourCurrentLocation')}</h3>`
            )
          )
          .addTo(newMap);

        // Group establishments by location to avoid duplicate pins
        const locationGroups: { [key: string]: Establishment[] } = {};

        // Group establishments by their coordinates
        route.forEach((item) => {
          const locationKey = `${item.location.lat.toFixed(6)},${item.location.lng.toFixed(6)}`;
          if (!locationGroups[locationKey]) {
            locationGroups[locationKey] = [];
          }
          locationGroups[locationKey].push(item);
        });

        // Add markers for each unique location
        let markerIndex = 0;
        Object.entries(locationGroups).forEach(([locationKey, establishments]) => {
          // Use the first establishment for location and risk grade
          const firstEstablishment = establishments[0];

          // Create pill-shaped marker as shown in the reference image
          const el = document.createElement('div');
          el.className = 'route-marker';
          el.style.zIndex = '10';

          // Main pill container
          const pillContainer = document.createElement('div');
          pillContainer.style.display = 'inline-flex';
          pillContainer.style.alignItems = 'center';
          pillContainer.style.backgroundColor = 'white';
          pillContainer.style.borderRadius = '20px';
          pillContainer.style.padding = '2px';
          pillContainer.style.boxShadow = '0 2px 4px rgba(0,0,0,0.3)';
          pillContainer.style.minWidth = '0';
          pillContainer.style.maxWidth = 'fit-content';
          pillContainer.style.height = '24px';

          // Left circle with number
          const leftCircle = document.createElement('div');
          leftCircle.style.width = '20px';
          leftCircle.style.height = '20px';
          leftCircle.style.borderRadius = '50%';
          leftCircle.style.backgroundColor = getRiskColor(firstEstablishment.risk_grade);
          leftCircle.style.color = 'white';
          leftCircle.style.display = 'flex';
          leftCircle.style.alignItems = 'center';
          leftCircle.style.justifyContent = 'center';
          leftCircle.style.fontWeight = 'bold';
          leftCircle.style.fontSize = '12px';
          leftCircle.innerText = `${markerIndex + 1}`;

          // Right side with count or risk grade
          const rightSide = document.createElement('div');
          rightSide.style.marginLeft = '4px';
          rightSide.style.marginRight = '8px';
          rightSide.style.color = '#333';
          rightSide.style.fontWeight = 'bold';
          rightSide.style.fontSize = '12px';
          rightSide.style.whiteSpace = 'nowrap';

          // Always show risk grade (inspection points)

          if (establishments.length > 1) {
            rightSide.innerText = `${(establishments.length * 1.1).toFixed(1)}`;
          } else {
            // Otherwise show risk grade
            rightSide.innerText = '1.1';
          }

          // Add small triangle pointer at bottom
          const pointer = document.createElement('div');
          pointer.style.position = 'absolute';
          pointer.style.bottom = '-8px';
          pointer.style.left = '50%';
          pointer.style.marginLeft = '-8px';
          pointer.style.width = '0';
          pointer.style.height = '0';
          pointer.style.borderLeft = '8px solid transparent';
          pointer.style.borderRight = '8px solid transparent';
          pointer.style.borderTop = '8px solid white';

          // Set marker offset to ensure the triangle points to the exact location
          const markerOptions = {
            element: el,
            anchor: 'bottom',
            offset: [0, 0]
          };

          // Assemble the pin
          pillContainer.appendChild(leftCircle);
          pillContainer.appendChild(rightSide);
          el.appendChild(pillContainer);
          el.appendChild(pointer);

          // Helper function to get color based on risk grade
          function getRiskColor(riskGrade: string) {
            switch(riskGrade) {
              case 'A': return '#4ade80'; // green
              case 'B': return '#a3e635'; // lime
              case 'C': return '#facc15'; // yellow
              case 'D': return '#fb923c'; // orange
              case 'E': return '#f87171'; // light red
              case 'F': return '#ef4444'; // red
              default: return '#3b82f6'; // blue
            }
          }

          // Create popup content with all establishments at this location
          let popupContent = '';
          establishments.forEach((est, i) => {
            popupContent += `
              <div class="${i > 0 ? 'mt-2 pt-2 border-t border-gray-200' : ''}">
                <h3 class="font-bold">${est.establishment.name_en}</h3>
                <p>${est.inspection_type.name_en}</p>
                ${
                  est.distance !== undefined && est.distance > 0
                    ? `<p>${t('common.distance')}: ${est.distance} km</p>`
                    : ''
                }
              </div>
            `;
          });

          new mapboxgl.Marker(markerOptions)
            .setLngLat([firstEstablishment.location.lng, firstEstablishment.location.lat])
            .setPopup(
              new mapboxgl.Popup({ offset: 25 }).setHTML(
                `<div class="establishment-popup">
                  ${popupContent}
                  ${establishments.length > 1 ?
                    `<div class="mt-2 text-xs text-blue-600 font-medium">
                      ${establishments.length} ${t('route.establishmentsAtLocation', 'establishments at this location')}
                    </div>` : ''
                  }
                </div>`
              )
            )
            .addTo(newMap);

          markerIndex++;
        });

        // Add route lines using Graphhopper path points if available
        let coordinates: [number, number][];

        if (pathPoints && pathPoints.length > 0) {
          // Use the detailed path from Graphhopper API
          // Ensure coordinates are in the correct format [lng, lat]
          coordinates = pathPoints.map(point => {
            // Graphhopper returns coordinates as [lng, lat]
            return point;
          });
          console.log(t('route.usingGraphhopperPoints', 'Using {{count}} points from Graphhopper API for route', { count: pathPoints.length }));
          console.log('Sample route points:', coordinates.slice(0, 3));
        } else {
          // Fallback to direct lines between points in the optimized order
          coordinates = [[startLocation.lng, startLocation.lat]];
          // Add each establishment location in the optimized order
          route.forEach((item) => {
            coordinates.push([item.location.lng, item.location.lat]);
          });
          console.log(t('route.usingDirectLines', 'Using direct lines between points for route'));
        }

        // Add the route source and layer
        newMap.addSource('route', {
          type: 'geojson',
          data: {
            type: 'Feature',
            properties: {},
            geometry: {
              type: 'LineString',
              coordinates: coordinates,
            },
          },
        });

        // Add route outline for better visibility
        newMap.addLayer({
          id: 'route-outline',
          type: 'line',
          source: 'route',
          layout: {
            'line-join': 'round',
            'line-cap': 'round',
          },
          paint: {
            'line-color': '#93c5fd',
            'line-width': 8,
            'line-opacity': 0.9,
          },
        });

        // Add route line with improved styling
        newMap.addLayer({
          id: 'route',
          type: 'line',
          source: 'route',
          layout: {
            'line-join': 'round',
            'line-cap': 'round',
          },
          paint: {
            'line-color': '#3b82f6',
            'line-width': 5,
            'line-opacity': 1.0,
            'line-dasharray': pathPoints && pathPoints.length > 0 ? undefined : [2, 1],
          },
        });

        // Add direction arrows along the route using line-dasharray
        if (pathPoints && pathPoints.length > 0) {
          // Add arrow effect using dash pattern
          newMap.addLayer({
            id: 'route-direction',
            type: 'line',
            source: 'route',
            layout: {
              'line-join': 'round',
              'line-cap': 'round',
            },
            paint: {
              'line-color': '#dbeafe',
              'line-width': 2,
              'line-opacity': 0.8,
              'line-dasharray': [0, 4, 3], // Creates a dash pattern that looks like arrows
            },
          });

          // Add arrow markers at regular intervals
          const routeLength = coordinates.length;
          if (routeLength > 10) {
            // Only add markers if we have enough points
            const interval = Math.max(Math.floor(routeLength / 5), 1); // Add ~5 arrows along the route

            for (let i = interval; i < routeLength - interval; i += interval) {
              const point = coordinates[i];
              const nextPoint = coordinates[i + 1];

              if (point && nextPoint) {
                // Calculate angle between points
                const angle = Math.atan2(
                  nextPoint[1] - point[1],
                  nextPoint[0] - point[0]
                ) * 180 / Math.PI;

                // Create arrow marker
                const arrowEl = document.createElement('div');
                arrowEl.className = 'route-arrow-marker';
                arrowEl.style.width = '12px';
                arrowEl.style.height = '12px';
                arrowEl.style.backgroundImage = 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' viewBox=\'0 0 24 24\' fill=\'%233b82f6\'%3E%3Cpath d=\'M5 12h14M12 5l7 7-7 7\'/%3E%3C/svg%3E")';
                arrowEl.style.backgroundSize = 'contain';

                // Add the marker
                new mapboxgl.Marker({
                  element: arrowEl,
                  rotation: angle
                })
                .setLngLat(point)
                .addTo(newMap);
              }
            }
          }
        }

        // Add route direction indicators
        if (coordinates.length > 2) {
          // Add arrow markers at regular intervals along the route
          const routeLength = coordinates.length;
          const interval = Math.max(Math.floor(routeLength / 8), 1); // Add ~8 arrows along the route

          for (let i = interval; i < routeLength - interval; i += interval) {
            const point = coordinates[i];
            const nextPoint = coordinates[Math.min(i + 1, routeLength - 1)];

            if (point && nextPoint) {
              // Calculate angle between points
              const angle = Math.atan2(
                nextPoint[1] - point[1],
                nextPoint[0] - point[0]
              ) * 180 / Math.PI;

              // Create arrow marker
              const arrowEl = document.createElement('div');
              arrowEl.className = 'route-arrow-marker';
              arrowEl.style.width = '16px';
              arrowEl.style.height = '16px';
              arrowEl.style.backgroundColor = '#3b82f6';
              arrowEl.style.borderRadius = '50%';
              arrowEl.style.border = '2px solid white';
              arrowEl.style.boxShadow = '0 0 5px rgba(0,0,0,0.5)';

              // Add the marker
              new mapboxgl.Marker({
                element: arrowEl,
                rotation: angle
              })
              .setLngLat(point)
              .addTo(newMap);
            }
          }
        }

        // Fit map to bounds
        newMap.fitBounds(bounds, {
          padding: { top: 50, bottom: 50, left: 50, right: 50 },
          maxZoom: 15,
        });
      });
    } catch (error) {
      console.error(t('map.errorInitializing', 'Error initializing map:'), error);
    }
  };

  // Function to start navigation mode
  const startNavigationMode = () => {
    if (!map.current || !userLocation || optimizedRoute.length === 0) return;

    setIsNavigationMode(true);
    setCurrentStep(0);
    setCurrentSegment(0);

    // Generate detailed directions based on the route
    const directions: string[] = [];
    const streetNames = [
      "Sheikh Zayed Road", "Al Khail Road", "Emirates Road",
      "Jumeirah Street", "Al Wasl Road", "Hessa Street",
      "Al Maktoum Street", "Financial Center Road", "Dubai-Al Ain Road"
    ];

    // Add starting direction
    if (optimizedRoute.length > 0) {
      const firstStop = optimizedRoute[0];
      const randomStreet = streetNames[crypto.getRandomValues(new Uint32Array(1))[0] % streetNames.length];
      directions.push(
        `Head north on ${randomStreet} towards ${firstStop.establishment.name_en}`
      );
    }

    // Add directions for each stop with more detailed instructions
    const cryptoRandomIndex = (max: number): number => {
      if (max <= 0) throw new Error("Max must be greater than 0");
      const maxUint32 = 0xFFFFFFFF;
      const limit = maxUint32 - (maxUint32 % max);
      const randArray = new Uint32Array(1);    
      while (true) {
        window.crypto.getRandomValues(randArray);
        if (randArray[0] < limit) {
          return randArray[0] % max;
        }
      }
    };    
    optimizedRoute.forEach((item, index) => {
      if (index === 0) return; // Skip first stop (starting point)

      const randomStreet = streetNames[cryptoRandomIndex(streetNames.length)];
      const direction = cryptoRandomIndex(2) === 0 ? "right" : "left"; // crypto-random left/right
      const distance = (cryptoRandomIndex(5) + 1); // random 1-5 km

      if (index % 3 === 0) {
        directions.push(`Turn ${direction} onto ${randomStreet}`);
      } else if (index % 3 === 1) {
        directions.push(`Continue straight for ${distance} km, then turn ${direction} towards ${item.establishment.name_en}`);
      } else {
        const exitNumber = cryptoRandomIndex(3) + 1; // random 1-3 exit
        directions.push(`At the roundabout, take the ${exitNumber}st exit onto ${randomStreet}`);
      }
    });    

    // Add final direction
    directions.push(`You have arrived at your destination`);

    setNavigationDirections(directions);

    // Set up the map for navigation mode
    if (map.current) {
      // Zoom to the first segment of the route
      const firstStop = optimizedRoute[0];

      // Create a smaller bounds that focuses on the current segment
      const bounds = new mapboxgl.LngLatBounds();
      bounds.extend([userLocation.lng, userLocation.lat]);
      bounds.extend([firstStop.location.lng, firstStop.location.lat]);

      map.current.fitBounds(bounds, {
        padding: { top: 100, bottom: 100, left: 100, right: 100 },
        maxZoom: 16,
      });

      // Create a pulsing user location marker if it doesn't exist
      if (!userMarkerRef.current) {
        const userEl = document.createElement('div');
        userEl.className = 'user-location-marker-navigation';
        userEl.style.width = '24px';
        userEl.style.height = '24px';
        userEl.style.borderRadius = '50%';
        userEl.style.backgroundColor = '#4285F4';
        userEl.style.border = '3px solid white';
        userEl.style.boxShadow = '0 0 10px rgba(0,0,0,0.3)';

        // Add pulsing effect
        const pulse = document.createElement('div');
        pulse.style.position = 'absolute';
        pulse.style.width = '40px';
        pulse.style.height = '40px';
        pulse.style.borderRadius = '50%';
        pulse.style.backgroundColor = 'rgba(66, 133, 244, 0.3)';
        pulse.style.border = '2px solid rgba(66, 133, 244, 0.5)';
        pulse.style.top = '-11px';
        pulse.style.left = '-11px';
        pulse.style.animation = 'pulse 1.5s infinite';
        userEl.appendChild(pulse);

        // Add CSS animation for pulsing effect
        const style = document.createElement('style');
        style.innerHTML = `
          @keyframes pulse {
            0% { transform: scale(0.8); opacity: 0.8; }
            70% { transform: scale(1.5); opacity: 0; }
            100% { transform: scale(0.8); opacity: 0; }
          }
        `;
        document.head.appendChild(style);

        userMarkerRef.current = new mapboxgl.Marker(userEl)
          .setLngLat([userLocation.lng, userLocation.lat])
          .addTo(map.current);
      }

      // Simulate movement along the route
      simulateMovement();

      // Automatically play the first direction using speech synthesis
      if (directions.length > 0) {
        setTimeout(() => {
          const utterance = new SpeechSynthesisUtterance(directions[0]);
          utterance.rate = 0.9; // Slightly slower
          utterance.pitch = 1.1; // Slightly higher pitch
          window.speechSynthesis.speak(utterance);
        }, 500); // Short delay to ensure everything is ready
      }
    }
  };

  // Function to simulate movement along the route
  const simulateMovement = () => {
    if (!map.current || !userLocation || optimizedRoute.length === 0 || !isNavigationMode) return;

    // This is a simplified simulation - in a real app, you would use the device's GPS
    // For demo purposes, we'll just move along the route at fixed intervals

    // Get the current segment's coordinates
    const currentDestination = optimizedRoute[currentStep];
    if (!currentDestination) return;

    // Move the user marker towards the destination
    const intervalId = setInterval(() => {
      if (!map.current || !userMarkerRef.current || !isNavigationMode) {
        clearInterval(intervalId);
        return;
      }

      // Get current position
      const currentPos = userMarkerRef.current.getLngLat();

      // Calculate direction to destination
      const destLng = currentDestination.location.lng;
      const destLat = currentDestination.location.lat;

      // Calculate step size (how much to move in each interval)
      // This is simplified - in a real app, you would use actual GPS data
      const stepSize = 0.0001; // Small step for demo purposes

      // Calculate distance to destination
      const distance = calculateDistance(
        currentPos.lat, currentPos.lng,
        destLat, destLng
      );

      // If we're close enough to the destination, move to the next segment
      if (distance < 0.05) { // Within 50 meters
        clearInterval(intervalId);

        // Move to next step
        if (currentStep < optimizedRoute.length - 1) {
          const nextStep = currentStep + 1;
          setCurrentStep(nextStep);

          // Play the next direction automatically
          setTimeout(() => {
            if (navigationDirections[nextStep]) {
              const utterance = new SpeechSynthesisUtterance(navigationDirections[nextStep]);
              utterance.rate = 0.9; // Slightly slower
              utterance.pitch = 1.1; // Slightly higher pitch
              window.speechSynthesis.speak(utterance);
            }
          }, 500);

          // After a short delay, start moving to the next destination
          setTimeout(() => {
            simulateMovement();
          }, 2000);
        }
        return;
      }

      // Calculate new position (move towards destination)
      const totalDistance = calculateDistance(
        currentPos.lat, currentPos.lng,
        destLat, destLng
      );

      // Normalize direction vector
      const dirLng = (destLng - currentPos.lng) / totalDistance;
      const dirLat = (destLat - currentPos.lat) / totalDistance;

      // Calculate new position
      const newLng = currentPos.lng + dirLng * stepSize;
      const newLat = currentPos.lat + dirLat * stepSize;

      // Update marker position
      userMarkerRef.current.setLngLat([newLng, newLat]);

      // Update map center to follow the user
      map.current.setCenter([newLng, newLat]);

    }, 100); // Update every 100ms for smooth movement
  };

  return (
    <div
      className="flex flex-col min-h-screen bg-gray-50"
      dir={language === 'ar' ? 'rtl' : 'ltr'}
    >
      <div className=" px-4 py-4 flex-1">
        {/* Navigation buttons */}
        <div className="flex justify-between mb-4">
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              className="flex items-center gap-1"
              onClick={() => {
                if (map.current && optimizedRoute.length > 0) {
                  // Create bounds to fit all points
                  const bounds = new mapboxgl.LngLatBounds();

                  // Add user location to bounds
                  if (userLocation) {
                    bounds.extend([userLocation.lng, userLocation.lat]);
                  }

                  // Add all establishment locations to bounds
                  optimizedRoute.forEach((item) => {
                    bounds.extend([item.location.lng, item.location.lat]);
                  });

                  // Fit map to bounds
                  map.current.fitBounds(bounds, {
                    padding: { top: 50, bottom: 50, left: 50, right: 50 },
                    maxZoom: 15,
                  });
                }
              }}
            >
              <Map className="w-4 h-4 mr-1" />
              {t('route.resetView', 'Reset View')}
            </Button>
          </div>

          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              className="flex items-center gap-1"
              onClick={calculateOptimalRoute}
              disabled={isCalculatingRoute}
            >
              {isCalculatingRoute ? (
                <Loader2 className="w-4 h-4 animate-spin mr-1" />
              ) : (
                <Route className="w-4 h-4 mr-1" />
              )}
              {t('route.recalculate', 'Recalculate')}
            </Button>

            <Button
              variant={isNavigationMode ? "destructive" : "default"}
              size="sm"
              className={`flex items-center gap-1 ${isNavigationMode ? 'bg-red-600 hover:bg-red-700' : 'bg-blue-600 hover:bg-blue-700'} text-white`}
              onClick={() => {
                if (isNavigationMode) {
                  // Exit navigation mode
                  setIsNavigationMode(false);
                  setCurrentStep(0);
                  setNavigationDirections([]);

                  // Reset the map view
                  if (map.current && optimizedRoute.length > 0 && userLocation) {
                    const bounds = new mapboxgl.LngLatBounds();
                    bounds.extend([userLocation.lng, userLocation.lat]);
                    optimizedRoute.forEach((item) => {
                      bounds.extend([item.location.lng, item.location.lat]);
                    });
                    map.current.fitBounds(bounds, {
                      padding: { top: 50, bottom: 50, left: 50, right: 50 },
                      maxZoom: 15,
                    });
                  }
                } else {
                  // Start navigation mode
                  if (map.current && optimizedRoute.length > 0 && userLocation) {
                    startNavigationMode();
                  }
                }
              }}
              disabled={!mapLoaded || optimizedRoute.length === 0}
            >
              <Navigation className="w-4 h-4 mr-1" />
              {isNavigationMode
                ? t('route.exitNavigation', 'Exit Navigation')
                : t('route.startNavigation', 'Start Navigation')}
            </Button>
          </div>
        </div>

        {/* Main content - Map and Route Info */}
        <div className="bg-white rounded-lg shadow-sm p-4 mb-4">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-bold">
              {isNavigationMode
                ? t('route.navigationMode', 'Navigation Mode')
                : t('route.optimizedRoute', 'Optimized Route')}
            </h2>

            {/* Route summary badge */}
            {routeInfo && (
              <div className="bg-[#b89256] text-white px-3 py-1 rounded-full text-sm font-medium">
                {routeInfo.totalDistance} {t('common.km', 'km')} • {routeInfo.totalTime} {t('route.minutes')}
              </div>
            )}
          </div>

          {/* Google Maps style navigation directions panel - only shown in navigation mode */}
          {isNavigationMode && navigationDirections.length > 0 && (
            <div className="bg-white rounded-lg shadow-md mb-4 overflow-hidden sticky top-0 z-10">
              {/* Current direction card with prominent display */}
              <div className="bg-blue-600 text-white p-4">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-bold text-lg">
                    {currentStep < navigationDirections.length - 1
                      ? t('route.nextDirection', 'Next Direction')
                      : t('route.arrived', 'Arrived')}
                  </h3>
                  <div className="text-sm bg-blue-700 px-2 py-1 rounded-full">
                    {currentStep + 1}/{navigationDirections.length}
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex items-center flex-1">
                    <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center mr-3 text-blue-600 font-bold text-xl">
                      {currentStep + 1}
                    </div>
                    <p className="text-xl font-medium">{navigationDirections[currentStep]}</p>
                  </div>

                  {/* Sound button */}
                  <button
                    className="ml-2 bg-blue-500 hover:bg-blue-400 rounded-full w-10 h-10 flex items-center justify-center"
                    onClick={() => {
                      // Use speech synthesis to read the direction
                      const utterance = new SpeechSynthesisUtterance(navigationDirections[currentStep]);
                      utterance.rate = 0.9; // Slightly slower
                      utterance.pitch = 1.1; // Slightly higher pitch
                      window.speechSynthesis.speak(utterance);
                    }}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"></polygon>
                      <path d="M15.54 8.46a5 5 0 0 1 0 7.07"></path>
                      <path d="M19.07 4.93a10 10 0 0 1 0 14.14"></path>
                    </svg>
                  </button>
                </div>
              </div>

              {/* Accordion for all directions */}
              <div className="max-h-60 overflow-y-auto">
                <div className="p-2 bg-gray-100 text-gray-700 font-medium text-sm border-b border-gray-200">
                  {t('route.allDirections', 'All Directions')}
                </div>

                {navigationDirections.map((direction, index) => (
                  <div
                    key={index}
                    className={`border-b border-gray-200 ${index === currentStep ? 'bg-blue-50' : ''}`}
                    onClick={() => {
                      // When clicking on a direction, center the map on that location
                      if (map.current && index < optimizedRoute.length) {
                        const location = optimizedRoute[Math.min(index, optimizedRoute.length - 1)].location;
                        map.current.flyTo({
                          center: [location.lng, location.lat],
                          zoom: 16,
                          speed: 1.5
                        });
                      }
                    }}
                  >
                    <div className="flex items-center p-3 cursor-pointer hover:bg-gray-50">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center mr-3 text-white font-bold ${index === currentStep ? 'bg-blue-600' : 'bg-gray-400'}`}>
                        {index + 1}
                      </div>
                      <div className="flex-1">
                        <p className={`${index === currentStep ? 'font-medium' : ''}`}>{direction}</p>
                      </div>

                      {/* Sound button for each direction */}
                      <button
                        className="ml-2 text-gray-500 hover:text-blue-600"
                        onClick={(e) => {
                          e.stopPropagation();
                          const utterance = new SpeechSynthesisUtterance(direction);
                          utterance.rate = 0.9;
                          window.speechSynthesis.speak(utterance);
                        }}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"></polygon>
                          <path d="M15.54 8.46a5 5 0 0 1 0 7.07"></path>
                        </svg>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Map container - full height in navigation mode */} {/* Loading indicator for map */}
          {/* <div
            ref={mapContainer}
            className={`w-full ${isNavigationMode ? 'h-[600px]' : 'h-[400px]'} rounded-lg mb-4 bg-gray-100 border border-gray-200`}
            style={{ position: 'relative', overflow: 'hidden' }}
          >

            {!mapLoaded && (
              <div className="absolute inset-0 flex items-center justify-center bg-gray-100 bg-opacity-80 z-10">
                <div className="flex flex-col items-center">
                  <Loader2 className="w-8 h-8 text-[#b89256] animate-spin mb-2" />
                  <p className="text-gray-600 text-sm">{t('map.loading')}</p>
                </div>
              </div>
            )}
          </div> */}

          {/* Route information cards */}
          <div className="flex flex-wrap gap-4 mb-2">
            {/* Starting point card */}
            <div className="bg-blue-50 rounded-lg p-3 flex items-center flex-1 min-w-[200px]">
              <div className="w-8 h-8 rounded-full bg-[#b89256] flex items-center justify-center mr-3">
                <MapPin className="w-4 h-4 text-white" />
              </div>
              <div>
                <div className="text-xs text-[#b89256] font-medium">{t('route.startingPoint')}</div>
                <div className="text-sm font-medium">
                  {userLocation ? t('route.yourLocation') : t('route.loading')}
                </div>
              </div>
            </div>

            {/* Stops card */}
            <div className="bg-amber-50 rounded-lg p-3 flex items-center flex-1 min-w-[200px]">
              <div className="w-8 h-8 rounded-full bg-[#b89256] flex items-center justify-center mr-3">
                <Navigation className="w-4 h-4 text-white" />
              </div>
              <div>
                <div className="text-xs text-[#b89256] font-medium">{t('route.totalStops')}</div>
                <div className="text-sm font-medium">
                  {optimizedRoute.length} {optimizedRoute.length === 1 ? t('route.stop') : t('route.stops')}
                </div>
              </div>
            </div>

            {/* Distance card */}
            {routeInfo && (
              <div className="bg-green-50 rounded-lg p-3 flex items-center flex-1 min-w-[200px]">
                <div className="w-8 h-8 rounded-full bg-[#b89256] flex items-center justify-center mr-3">
                  <Route className="w-4 h-4 text-white" />
                </div>
                <div>
                  <div className="text-xs text-[#b89256] font-medium">{t('route.totalDistance')}</div>
                  <div className="text-sm font-medium">{routeInfo.totalDistance} {t('common.km', 'km')}</div>
                </div>
              </div>
            )}

            {/* Optimization Method card */}
            {routeInfo && false && (
              <div className="bg-purple-50 rounded-lg p-3 flex items-center flex-1 min-w-[200px]">
                <div className="w-8 h-8 rounded-full bg-[#b89256] flex items-center justify-center mr-3">
                  <Map className="w-4 h-4 text-white" />
                </div>
                <div>
                  <div className="text-xs text-[#b89256] font-medium">{t('route.optimizationMethod', 'Optimization Method')}</div>
                  <div className="text-sm font-medium">{routeInfo.optimizationMethod}</div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Inspection list - hidden in navigation mode */}
        {!isNavigationMode && (
          <div className="bg-white rounded-lg shadow-sm p-4">
          <h2 className="text-xl font-bold mb-4">{t('route.inspectionList')}</h2>

          {loading ? (
            <div className="flex flex-col items-center justify-center py-8">
              <Loader2 className="w-8 h-8 text-[#b89256] animate-spin mb-2" />
              <p className="text-gray-600">{t('common.loading')}</p>
            </div>
          ) : error ? (
            <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-4">
              <div className="flex items-center">
                <AlertTriangle className="w-5 h-5 text-red-500 mr-2" />
                <p className="text-red-700">{error}</p>
              </div>
            </div>
          ) : (
            <>
              {optimizedRoute.length > 0 ? (
                <div className="space-y-4">
                  {optimizedRoute.map((item, index) => (
                    <div key={item.id} className="mb-4  rounded-lg p-3 hover:bg-gray-50">
                      <div className="flex items-center mb-3">

                        {/* {item.distance !== undefined && item.distance > 0 && (
                          <div className="text-sm bg-blue-50 text-blue-700 px-3 py-1 rounded-full flex items-center">
                            <Route className="w-3 h-3 mr-1" />
                            <span className="font-medium">{item.distance} {t('common.km', 'km')}</span>
                          </div>
                        )} */}
                      </div>

                      {/* Inspection card */}
                      <InspectionCard
                        id={item.id}
                        establishment_id={item.establishment_id}
                        establishment={item.establishment}
                        emirate={item.emirate}
                        inspection_type={item.inspection_type}
                        risk_grade={item.risk_grade}
                        status={item.status}
                        language={language}
                        distance={item.distance}
                      />

                      {/* Distance information below card */}
                      {item.distance !== undefined && item.distance > 0 && (
                        <div className="mt-2 text-sm text-gray-600 flex items-center">
                          <div className="w-5 h-5 rounded-full text-[#b89256] bg-white border border-[#b89256] flex items-center justify-center text-xs font-bold mr-2">
                            {index + 1}
                          </div>
                          <Navigation className="w-4 h-4 mr-1 text-[#b89256]" />
                          <span>
                            {t('route.distanceFromPrevious')}: <span className="font-medium">{item.distance} {t('common.km', 'km')}</span>
                          </span>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <Map className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                  <p className="text-gray-600 mb-4">
                    {isCalculatingRoute
                      ? t('route.calculatingRoute')
                      : t('route.noAssignedTasks')}
                  </p>
                  {!isCalculatingRoute && assignedTasks.length === 0 && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => navigate('/suggested-inspections')}
                    >
                      {t('route.goToSuggestedInspections')}
                    </Button>
                  )}
                </div>
              )}
            </>
          )}
        </div>
        )}
      </div>
    </div>
  );
}
